import React from 'react';
import { useIntl } from 'umi';
import IconAppstore from '@intelligence/assets/imgs/CasualWordlWide/icon-appstore.png';
import Bubble from '@home/components/TeaComponents/Bubble';
import GameImage from '@home/components/GameImage';
import { gotoIntelligenceGame } from '@home/utils/utils';
import { getCountryFlag } from '@home/_config/countries';
import { getGenreNameI18n } from '@intelligence/v3/utils/genre';
import StyledSubtitle from '../StyledSubtitle';
import { getRegionConfig } from '../../constants';
import './index.scss';

/** mobile.top_list[] 接口字段 */
interface MobileTopListItem {
    game_id: string;
    unified_id: string;
    game_name: string;
    cover: string;
    genre: string;
    sub_genre: string;
    publisher: string;
    publisher_country_abbre: string;
    appstore__rank: number | null;
    appstore__rank_growth: number | null | undefined;
}

/** mobile.anomalies[] 接口字段 */
interface MobileAnomalyItem {
    game_id: string;
    unified_id: string;
    game_name: string;
    cover: string;
    genre: string;
    sub_genre: string;
    publisher: string;
    publisher_country_abbre: string;
    change_type: string;
    change_reason: string;
    release_time: string;
    summary_zh: string;
    summary_en: string;
    appstore__rank: number | null;
    appstore__rank_growth: number | null | undefined;
}

/** mobile[] 接口字段 */
export interface MobileRegionData {
    country_region: string;
    top_list: MobileTopListItem[];
    anomalies: MobileAnomalyItem[];
}

interface IMobileBreakoutsProps {
    data: MobileRegionData[];
    reportDate?: string;
    locale?: string;
    genreOptionsMap?: IRecord<any[]>;
}

/** 获取排名增长的展示类型（null/undefined：无昨日名次对比，展示 New；0：持平 =） */
const getRankGrowthType = (
    growth: number | null | undefined,
): 'new' | 'up' | 'down' | 'equal' => {
    if (growth == null) return 'new';
    if (growth === 0) return 'equal';
    return growth > 0 ? 'up' : 'down';
};

/** 格式化排名增长数值（仅升降） */
const formatRankGrowth = (growth: number): string =>
    growth > 0 ? `+${growth}` : `${growth}`;

const MobileBreakouts: React.FC<IMobileBreakoutsProps> = ({ data, locale, genreOptionsMap }) => {
    const { formatMessage } = useIntl();
    const isZh = locale === 'zh-CN';

    const translateGenre = (genreValue: string) =>
        genreValue.split('|').map((v) => getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource: 'iegg', genreValue: v.trim() })).join('|');

    if (!data || data.length === 0) {
        return null;
    }

    // 跳转游戏详情
    const handleGoToGameDetails = (gameId: string) => {
        if (gameId) {
            gotoIntelligenceGame(gameId, 'mobile', 'stats');
        }
    };

    const renderTags = (genre: string, subGenre: string) => {
        const allTags = `${genre}|${subGenre}`.split('|').filter(Boolean);
        const uniqueTags = Array.from(new Set(allTags.map((v) => v.trim())));
        
        return uniqueTags.map((tag, idx) => {
            const name = getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource: 'iegg', genreValue: tag });
            return (
                <Bubble content={name} key={idx}>
                    <div className="dbi-worldwide-game-tag">{name}</div>
                </Bubble>
            );
        });
    };

    const renderTagsWorldwide = (genre: string, subGenre: string) => {
        const allTags = `${genre}|${subGenre}`.split('|').filter(Boolean);
        const uniqueTags = Array.from(new Set(allTags.map((v) => v.trim())));
        if (uniqueTags.length <= 2){
            return uniqueTags.map((tag, idx) => {
                const name = getGenreNameI18n({ locale, genreOptionsMap: genreOptionsMap || {}, genreSource: 'iegg', genreValue: tag });
                return (
                    <Bubble content={name} key={idx}>
                        <div className="dbi-worldwide-game-tag">{name}</div>
                    </Bubble>
                );
            });
        }else {
            return (
                <>
                    <div className="dbi-worldwide-game-tags__row dbi-worldwide-game-tags__row--top">
                        {uniqueTags.slice(0, 2).map((tag, idx) => {
                            const name = getGenreNameI18n({
                                locale,
                                genreOptionsMap: genreOptionsMap || {},
                                genreSource: 'iegg',
                                genreValue: tag,
                            });

                            return (
                                <Bubble content={name} key={idx}>
                                    <div 
                                        className="dbi-worldwide-game-tag" 
                                        style={{ 
                                            flex: idx === 1 ? '0 1 auto' : '0 0 auto',
                                            minWidth: idx === 1 ? '0' : 'auto',
                                            overflow: idx === 1 ? 'hidden' : 'visible',
                                            textOverflow: idx === 1 ? 'ellipsis' : 'clip',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {name}
                                    </div>
                                </Bubble>
                            );
                        })}
                    </div>
                    <div className="dbi-worldwide-game-tags__row dbi-worldwide-game-tags__row--bottom">
                        {uniqueTags.slice(2).map((tag, idx) => {
                            const name = getGenreNameI18n({
                            locale,
                            genreOptionsMap: genreOptionsMap || {},
                            genreSource: 'iegg',
                            genreValue: tag,
                            });

                            return (
                            <Bubble content={name} key={idx}>
                                <div className="dbi-worldwide-game-tag">{name}</div>
                            </Bubble>
                            );
                        })}
                    </div>
                </>
            );
        }
    };
    
    // 渲染游戏卡片
    const renderGameCard = (
        game: { unified_id: string; cover: string; game_name: string; publisher: string; genre: string; sub_genre: string }, 
        countryCode: string, 
        tagRenderer: (genre: string, sub_genre: string) => React.ReactNode
    ) => (
        <div className="dbi-worldwide-game-card">
            <div className="dbi-worldwide-game-icon">
                <GameImage entityType="mobile" src={game.cover} alt={game.game_name} />
            </div>
            <div className="dbi-worldwide-game-info">
                <div className="dbi-worldwide-game-name">
                    <Bubble content={game.game_name}>
                        <span>{game.game_name}</span>
                    </Bubble>
                </div>
                <div className="dbi-worldwide-game-country">
                    {countryCode && (
                        <img className="dbi-worldwide-game-country-img" src={getCountryFlag(countryCode)} alt={countryCode} />
                    )}
                    <Bubble content={game.publisher}>
                        <span className="dbi-worldwide-game-country-text">{game.publisher}</span>
                    </Bubble>
                </div>
                <div className="dbi-worldwide-game-tags">
                    {tagRenderer(game.genre, game.sub_genre)}
                </div>
            </div>
        </div>
    );

    // 渲染收入榜排名变化：无昨日对比(null/undefined)→新进榜/ New 蓝框标签；0→持平；否则升降数值
    const renderIncome = (
        rank: number | null,
        growth: number | null | undefined,
    ) => {
        const trendType = getRankGrowthType(growth);

        return (
            <div className="dbi-worldwide-game-income">
                <div className="dbi-worldwide-game-income-hd">
                    <img src={IconAppstore} alt="" />
                    <span className="dbi-worldwide-game-income-title">{formatMessage({ id: 'dbi_worldwide_grossing_upper' })}</span>
                </div>
                <div className="dbi-worldwide-game-income-bd">
                    <span className="dbi-worldwide-game-income-num">{rank !== null ? `#${rank}` : '-'}</span>
                    {trendType === 'equal' ? (
                        <span className="dbi-worldwide-game-income-trend --equal"></span>
                    ) : trendType === 'new' ? (
                        <span className="dbi-worldwide-game-income-trend --new-entry">
                            {formatMessage({ id: 'dbi_worldwide_new_entry' })}
                        </span>
                    ) : (
                        <span className={`dbi-worldwide-game-income-trend --${trendType}`}>
                            {formatRankGrowth(growth as number)}
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderAnomalyRankTrend = (
        growth: number | null | undefined,
    ): React.ReactNode => {
        const t = getRankGrowthType(growth);
        if (t === 'equal') {
            return <span className="dbi-worldwide-anomaly-income-trend --equal" />;
        }
        if (t === 'new') {
            return (
                <span className="dbi-worldwide-anomaly-income-trend --new-entry">
                    {formatMessage({ id: 'dbi_worldwide_new_entry' })}
                </span>
            );
        }
        return (
            <span className={`dbi-worldwide-anomaly-income-trend --${t}`}>
                {formatRankGrowth(growth as number)}
            </span>
        );
    };

    // 渲染地区板块
    const renderRegionCard = (region: MobileRegionData) => {
        const config = getRegionConfig(region.country_region);

        return (
            <div className={`dbi-worldwide-region-card ${config.className}`} key={region.country_region}>
                <div className="dbi-worldwide-region-card-inner">
                    <div className="dbi-worldwide-region-header">
                        {config.flagImg && (
                            <div className="dbi-worldwide-flag">
                                <img src={config.flagImg} alt={region.country_region} />
                            </div>
                        )}
                        <div className="dbi-worldwide-region-name">{isZh ? config.nameZh : config.nameEn}</div>
                    </div>
                    {/* Top List */}
                    <div className="dbi-worldwide-top3">
                        {region.top_list.map((game, idx) => (
                            <div className="dbi-worldwide-game-item" key={game.unified_id || idx} onClick={() => handleGoToGameDetails(game.unified_id)} style={{ cursor: 'pointer' }}>
                                {renderGameCard(game, config.countryCode, renderTagsWorldwide)}
                                {renderIncome(game.appstore__rank, game.appstore__rank_growth)}
                            </div>
                        ))}
                    </div>
                    {/* 异动 */}
                    {region.anomalies && region.anomalies.length > 0 && (
                        <div className="dbi-worldwide-anomaly-list">
                            {region.anomalies.map((item, idx) => (
                                <div className="dbi-worldwide-anomaly-item" key={item.game_id || idx} onClick={() => handleGoToGameDetails(item.unified_id)} style={{ cursor: 'pointer' }}>
                                    <div className={`dbi-worldwide-anomaly-badge --${item.change_type === '新上线' ? 'new' : 'increase'}`}>
                                        {item.change_type === '新上线' ? '新上线' : '突增'}
                                    </div>
                                    {renderGameCard(item, config.countryCode, renderTags)}
                                    <div className="dbi-worldwide-anomaly-intro">
                                        <div className="dbi-worldwide-anomaly-intro-top">
                                            <div className="dbi-worldwide-anomaly-income">
                                                <img src={IconAppstore} alt="" />
                                                <span className="dbi-worldwide-anomaly-income-title">{formatMessage({ id: 'dbi_worldwide_grossing' })}</span>
                                                <span className="dbi-worldwide-anomaly-income-num">{item.appstore__rank !== null ? `#${item.appstore__rank}` : '-'}</span>
                                                {renderAnomalyRankTrend(item.appstore__rank_growth)}
                                            </div>
                                            {item.release_time && (
                                                <div className="dbi-worldwide-anomaly-income-release">{formatMessage({ id: 'dbi_worldwide_game_release' })} {item.release_time?.substring(0, 10)}</div>
                                            )}
                                        </div>
                                        <div className="dbi-worldwide-anomaly-intro-text">
                                            {isZh ? item.summary_zh : item.summary_en}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="dbi-worldwide-tabcontent">
            <div className="dbi-worldwide-title-wrap">
                <StyledSubtitle text={formatMessage({ id: 'dbi_worldwide_mobile_breakouts' })} />
                <div className="dbi-worldwide-tips">
                    <div className="dbi-worldwide-tips-item">
                        <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_new_label' })}</b>
                        {formatMessage({ id: 'dbi_worldwide_mobile_tip_new_desc' })}
                    </div>
                    <div className="dbi-worldwide-tips-item">
                        <b>{formatMessage({ id: 'dbi_worldwide_mobile_tip_surge_label' })}</b>
                        {formatMessage({ id: 'dbi_worldwide_mobile_tip_surge_desc' })}
                    </div>
                </div>
            </div>
            {data.map((region) => renderRegionCard(region))}
        </div>
    );
};

export default MobileBreakouts;

